import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { AMPLITUDE_API_KEY, IS_VERCEL } from '../constants/env'
import { BASE_URL } from '../constants/routes'
import { ampli, Ampli, Event } from '../ampli'
import { useSmartSignalsContext } from '../context/BotdContext'

interface AmpliContextValue {
  ampli: Ampli
  isClientLoaded: boolean
  botDetected: string
  setHeaderPresent: (present: boolean) => void
}

const AmpliContext = createContext<AmpliContextValue>({
  ampli: ampli,
  isClientLoaded: false,
  botDetected: 'False',
  setHeaderPresent: () => {},
})

export enum ExperimentValue {
  Control = 'control',
  Treatment = 'treatment',
}

export enum ExperimentFlags {
  AiInstallHomepageCta = 'ai-install-homepage-cta-2026',
}

const EVENT_TYPE = 'Web Page Viewed'

/**
 * This is a plugin that renames the Page view event_properties according to our analytics needs
 * @returns EnrichmentPlugin
 */

const renameEventPropertiesEnrichment = (botDetected: string, vmDetected: boolean, signupOptionsShown: boolean) => ({
  name: 'rename-event-properties-enrichment',
  type: 'enrichment' as const,
  async setup() {
    return
  },
  async execute(event: Event) {
    // Only apply to Web Page View events
    if (event.event_type !== EVENT_TYPE) {
      return event
    }

    // Rename event properties and add botDetected
    const originalEventProperties = event.event_properties
    originalEventProperties &&
      (event.event_properties = {
        'Web Page Domain': originalEventProperties['[Amplitude] Page Domain'],
        'Web Page Location': originalEventProperties['[Amplitude] Page Location'],
        'Web Page Path': originalEventProperties['[Amplitude] Page Path'],
        'Web Page Title': originalEventProperties['[Amplitude] Page Title'],
        'Web Page URL': originalEventProperties['[Amplitude] Page URL'],
        botDetected: botDetected,
        vmDetected: vmDetected,
        signupOptionsShown: signupOptionsShown,
      })

    return event
  },
})

export const AmpliProvider = ({ children }: { children: React.ReactNode }) => {
  const { currentEvent, visitorId } = useSmartSignalsContext()
  const [botDetected, setBotDetected] = useState<string>('False')
  const [isClientLoaded, setIsClientLoaded] = useState(false)
  const [headerPresent, setHeaderPresent] = useState(false)

  const setUserData = useCallback(
    (currentVisitorId?: string) => {
      if (!isClientLoaded || !ampli.client || !currentVisitorId) {
        return
      }
      ampli.client.setDeviceId(currentVisitorId)
      ampli.client.setGroup('fingerprint-device-id', currentVisitorId)
    },
    [isClientLoaded]
  )

  useEffect(() => {
    if (!visitorId || !currentEvent?.bot) return

    ampli
      .load({
        client: {
          apiKey: AMPLITUDE_API_KEY,
          configuration: {
            serverUrl: IS_VERCEL ? `${BASE_URL}/9QlX9T00f/` : undefined,
            deviceId: visitorId,
            identityStorage: 'localStorage',
            defaultTracking: {
              attribution: true,
              pageViews: {
                eventType: EVENT_TYPE,
                trackOn: () => {
                  // Only letters, numbers and dashes are allowed in the pathname
                  return /^[a-zA-Z0-9/-]*$/.test(window.location.pathname)
                },
              },
              sessions: false,
              formInteractions: false,
              fileDownloads: false,
            },
          },
        },
      })
      .promise.then(() => {
        const botDetectedData = currentEvent?.bot === 'bad' ? 'True' : 'False'
        const vmDetectedData = Boolean(currentEvent?.virtual_machine)
        ampli.client.add(renameEventPropertiesEnrichment(botDetectedData, vmDetectedData, headerPresent))
        setIsClientLoaded(true)
      })
  }, [visitorId, currentEvent, headerPresent])

  useEffect(() => {
    if (isClientLoaded && visitorId) {
      setUserData(visitorId)
    }
  }, [visitorId, setUserData, isClientLoaded])

  useEffect(() => {
    if (isClientLoaded && currentEvent?.bot) {
      const botDetected = currentEvent.bot === 'bad' ? 'True' : 'False'
      setBotDetected(botDetected)
      ampli.identify(undefined, { botDetected })
    }
  }, [isClientLoaded, currentEvent])

  // Re-add enrichment plugin when headerPresent changes
  useEffect(() => {
    if (isClientLoaded && ampli.client) {
      const botDetectedData = currentEvent?.bot === 'bad' ? 'True' : 'False'
      const vmDetectedData = Boolean(currentEvent?.virtual_machine)
      // Remove existing enrichment plugin and add new one with updated headerPresent value
      ampli.client.remove('rename-event-properties-enrichment')
      ampli.client.add(renameEventPropertiesEnrichment(botDetectedData, vmDetectedData, headerPresent))
    }
  }, [isClientLoaded, headerPresent, currentEvent])

  return (
    <AmpliContext.Provider value={{ ampli, isClientLoaded, botDetected, setHeaderPresent }}>
      {children}
    </AmpliContext.Provider>
  )
}

export const useAmpli = () => useContext(AmpliContext)
